// useTableControls — search, filter, sort, pagination, time range, export, saved views
import { useState, useMemo, useCallback } from 'react';
import ExcelJS from 'exceljs';

export type SortDir = 'asc' | 'desc' | null;

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
  accessor?: (row: T) => string | number | null | undefined;
  filterOptions?: { value: string; label: string }[];
  hideable?: boolean; // can be toggled in column visibility
  dateField?: boolean; // marks this column as a date for time range filtering
}

export interface TimeRange {
  field: string;
  start: string; // ISO date string
  end: string;   // ISO date string
}

export interface SavedView {
  id: string;
  name: string;
  search: string;
  filters: Record<string, string>;
  sortKey: string | null;
  sortDir: SortDir;
  timeRange: TimeRange | null;
  hiddenColumns: string[];
}

export interface QuickFilter<T> {
  label: string;
  test: (row: T) => boolean;
}

export interface TableControls<T> {
  search: string;
  setSearch: (v: string) => void;
  sortKey: string | null;
  sortDir: SortDir;
  setSort: (key: string) => void;
  page: number;
  setPage: (v: number) => void;
  pageSize: number;
  setPageSize: (v: number) => void;
  activeFilters: Record<string, string>;
  setFilter: (key: string, value: string) => void;
  clearFilters: () => void;
  timeRange: TimeRange | null;
  setTimeRange: (tr: TimeRange | null) => void;
  hiddenColumns: string[];
  toggleColumn: (key: string) => void;
  visibleColumns: Column<T>[];
  selectedIds: Set<string>;
  toggleSelect: (id: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  savedViews: SavedView[];
  saveView: (name: string) => void;
  loadView: (id: string) => void;
  deleteView: (id: string) => void;
  processed: T[];
  paged: T[];
  totalPages: number;
  totalFiltered: number;
  exportCSV: () => void;
  exportExcel: () => void;
}

const VIEWS_KEY = 'atlas_table_views';

export function useTableControls<T extends Record<string, any>>(
  data: T[],
  columns: Column<T>[],
  options?: {
    defaultPageSize?: number;
    defaultSortKey?: string;
    defaultSortDir?: SortDir;
    quickFilters?: QuickFilter<T>[];
    keyAccessor?: (row: T) => string;
    storageKey?: string; // for persisting views per-table
  }
): TableControls<T> {
  const { defaultPageSize = 25, defaultSortKey, defaultSortDir = 'asc', storageKey } = options || {};

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey || null);
  const [sortDir, setSortDir] = useState<SortDir>(defaultSortKey ? defaultSortDir : null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => {
    const key = storageKey ? `${VIEWS_KEY}_${storageKey}` : VIEWS_KEY;
    try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
  });

  const setSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc');
      if (sortDir === 'desc') setSortKey(null);
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const setFilter = (key: string, value: string) => {
    setActiveFilters(prev => {
      const next = { ...prev };
      if (!value || value === '') delete next[key];
      else next[key] = value;
      return next;
    });
    setPage(1);
  };

  const clearFilters = () => {
    setActiveFilters({});
    setSearch('');
    setTimeRange(null);
    setPage(1);
  };

  const toggleColumn = (key: string) => {
    setHiddenColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const visibleColumns = useMemo(
    () => columns.filter(c => !hiddenColumns.includes(c.key)),
    [columns, hiddenColumns]
  );

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    const keyAccessor = options?.keyAccessor || ((row: T) => row.id || String(row));
    setSelectedIds(prev => {
      const next = new Set(prev);
      for (const row of data) next.add(keyAccessor(row));
      return next;
    });
  }, [data, options?.keyAccessor]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const persistViews = useCallback((views: SavedView[]) => {
    setSavedViews(views);
    const key = storageKey ? `${VIEWS_KEY}_${storageKey}` : VIEWS_KEY;
    localStorage.setItem(key, JSON.stringify(views));
  }, [storageKey]);

  const saveView = useCallback((name: string) => {
    const view: SavedView = {
      id: crypto.randomUUID(),
      name,
      search,
      filters: { ...activeFilters },
      sortKey,
      sortDir,
      timeRange: timeRange ? { ...timeRange } : null,
      hiddenColumns: [...hiddenColumns],
    };
    persistViews([...savedViews, view]);
  }, [search, activeFilters, sortKey, sortDir, timeRange, hiddenColumns, savedViews, persistViews]);

  const loadView = useCallback((id: string) => {
    const view = savedViews.find(v => v.id === id);
    if (!view) return;
    setSearch(view.search);
    setActiveFilters(view.filters);
    setSortKey(view.sortKey);
    setSortDir(view.sortDir);
    setTimeRange(view.timeRange);
    setHiddenColumns(view.hiddenColumns);
    setPage(1);
  }, [savedViews]);

  const deleteView = useCallback((id: string) => {
    persistViews(savedViews.filter(v => v.id !== id));
  }, [savedViews, persistViews]);

  // Core processing pipeline
  const processed = useMemo(() => {
    let result = [...data];

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(row =>
        columns.some(col => {
          const val = col.accessor ? col.accessor(row) : row[col.key];
          return val != null && String(val).toLowerCase().includes(q);
        })
      );
    }

    // Filters
    for (const [key, value] of Object.entries(activeFilters)) {
      if (!value) continue;
      result = result.filter(row => {
        const val = row[key];
        return val != null && String(val).toLowerCase() === value.toLowerCase();
      });
    }

    // Time range
    if (timeRange) {
      const start = new Date(timeRange.start).getTime();
      const end = new Date(timeRange.end).getTime();
      result = result.filter(row => {
        const val = row[timeRange.field];
        if (!val) return false;
        const t = new Date(val).getTime();
        return t >= start && t <= end;
      });
    }

    // Sort
    if (sortKey && sortDir) {
      const col = columns.find(c => c.key === sortKey);
      const accessor = col?.accessor || ((row: T) => row[sortKey]);
      result.sort((a, b) => {
        const av = accessor(a);
        const bv = accessor(b);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') {
          return sortDir === 'asc' ? av - bv : bv - av;
        }
        const cmp = String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [data, search, activeFilters, timeRange, sortKey, sortDir, columns]);

  const totalFiltered = processed.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = processed.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Export functions
  const exportCSV = useCallback(() => {
    const visibleCols = columns.filter(c => !hiddenColumns.includes(c.key));
    const header = visibleCols.map(c => c.label).join(',');
    const rows = processed.map(row =>
      visibleCols.map(col => {
        const val = col.accessor ? col.accessor(row) : row[col.key];
        const str = val == null ? '' : String(val);
        // Escape CSV
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      }).join(',')
    );
    const csv = '\uFEFF' + [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `atlas-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [processed, columns, hiddenColumns]);

  const exportExcel = useCallback(async () => {
    const visibleCols = columns.filter(c => !hiddenColumns.includes(c.key));
    const rows = processed.map(row =>
      visibleCols.map(col => {
        const val = col.accessor ? col.accessor(row) : row[col.key];
        return val == null ? '' : val;
      })
    );
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Export');
    ws.columns = visibleCols.map((col, i) => {
      const maxLen = Math.max(
        col.label.length,
        ...rows.slice(0, 100).map(r => String(r[i] ?? '').length)
      );
      return { header: col.label, key: col.key, width: Math.min(maxLen + 2, 50) };
    });
    rows.forEach(row => ws.addRow(row));
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `atlas-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [processed, columns, hiddenColumns]);

  return {
    search, setSearch,
    sortKey, sortDir, setSort,
    page: safePage, setPage,
    pageSize, setPageSize,
    activeFilters, setFilter, clearFilters,
    timeRange, setTimeRange,
    hiddenColumns, toggleColumn, visibleColumns,
    selectedIds, toggleSelect, selectAll, clearSelection,
    savedViews, saveView, loadView, deleteView,
    processed, paged,
    totalPages, totalFiltered,
    exportCSV, exportExcel,
  };
}
